import React, { useState, useEffect } from 'react';
import { Bell, Mail, Smartphone, Clock, Calendar } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';

export const NotificationsStep: React.FC = () => {
  const { userPreferences, saveUserPreferences } = useOnboarding();
  const [notifications, setNotifications] = useState({
    budget: true,
    weekly: false,
    monthly: true
  });

  // Cargar configuración desde las preferencias
  useEffect(() => {
    if (userPreferences?.notifications) {
      setNotifications(userPreferences.notifications);
    }
  }, [userPreferences]);

  const handleToggleNotification = (type: keyof typeof notifications) => {
    const newNotifications = {
      ...notifications,
      [type]: !notifications[type]
    };
    setNotifications(newNotifications);
    saveUserPreferences({ notifications: newNotifications });
  };

  const notificationOptions = [
    {
      id: 'budget',
      title: 'Alertas de Presupuesto',
      description: 'Te avisamos cuando te acerques al límite de tus categorías',
      icon: <Bell className="w-6 h-6 text-red-500" />,
      recommended: true
    },
    {
      id: 'weekly',
      title: 'Resumen Semanal',
      description: 'Un reporte de tus gastos cada domingo',
      icon: <Clock className="w-6 h-6 text-blue-500" />,
      recommended: false
    },
    {
      id: 'monthly',
      title: 'Reporte Mensual',
      description: 'Análisis completo de tus finanzas cada mes',
      icon: <Calendar className="w-6 h-6 text-green-500" />,
      recommended: true
    }
  ];

  const activeNotifications = Object.values(notifications).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          Configura tus notificaciones
        </h4>
        <p className="text-gray-600">
          Elige cómo quieres recibir información sobre tus finanzas
        </p>
      </div>

      {/* Tipos de notificaciones */}
      <div className="space-y-4">
        {notificationOptions.map((option) => (
          <div
            key={option.id}
            className="flex items-start space-x-4 p-4 bg-white border border-gray-200 rounded-lg"
          >
            <div className="flex-shrink-0 mt-1">
              {option.icon}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h5 className="font-medium text-gray-900">
                  {option.title}
                </h5>
                {option.recommended && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Recomendado
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {option.description}
              </p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications[option.id as keyof typeof notifications]}
                onChange={() => handleToggleNotification(option.id as keyof typeof notifications)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        ))}
      </div>

      {/* Métodos de notificación */}
      <div>
        <h5 className="font-medium text-gray-900 mb-3">
          ¿Cómo quieres recibir las notificaciones?
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Smartphone className="w-6 h-6 text-blue-600" />
            <div>
              <h6 className="font-medium text-blue-900">En la aplicación</h6>
              <p className="text-sm text-blue-700">
                Notificaciones directas en tu navegador
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg opacity-50">
            <Mail className="w-6 h-6 text-gray-400" />
            <div>
              <h6 className="font-medium text-gray-500">Por email</h6>
              <p className="text-sm text-gray-400">
                Próximamente disponible
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vista previa de notificaciones */}
      {activeNotifications > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h6 className="font-medium text-gray-900 mb-3">
            Vista previa de tus notificaciones
          </h6>
          <div className="space-y-2">
            {notifications.budget && (
              <div className="flex items-center space-x-2 text-sm">
                <Bell className="w-4 h-4 text-red-500" />
                <span className="text-gray-700">
                  "Has gastado 80% de tu presupuesto en Alimentación"
                </span>
              </div>
            )}
            {notifications.weekly && (
              <div className="flex items-center space-x-2 text-sm">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-gray-700">
                  "Resumen semanal: gastaste €234 esta semana"
                </span>
              </div>
            )}
            {notifications.monthly && (
              <div className="flex items-center space-x-2 text-sm">
                <Calendar className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">
                  "Reporte mensual: tus gastos de enero"
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Información de privacidad */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Bell className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h6 className="font-medium text-yellow-900 mb-1">
              Control total de tu privacidad
            </h6>
            <p className="text-sm text-yellow-800">
              Las notificaciones se generan localmente en tu dispositivo. 
              Puedes cambiar estas preferencias en cualquier momento desde la configuración.
            </p>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="text-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h6 className="font-medium text-blue-900 mb-1">
          {activeNotifications === 0 
            ? "Sin notificaciones activas" 
            : `${activeNotifications} tipo${activeNotifications !== 1 ? 's' : ''} de notificación activa${activeNotifications !== 1 ? 's' : ''}`
          }
        </h6>
        <p className="text-sm text-blue-700">
          {activeNotifications === 0
            ? "Puedes activar notificaciones más tarde si cambias de opinión"
            : "Te mantendremos informado según tus preferencias"
          }
        </p>
      </div>
    </div>
  );
};